import React from 'react';
import classNames from 'classnames/bind';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

import { Gateway } from 'types/gateway';

import { VideoSource, useVideoSources } from 'hooks/api/useVideoSources';
import { useGateways } from 'gateways/hooks/useGateways';
import { useStartCloudGateway } from 'gateways/hooks/useStartCloudGateway';
import { useMqttGatewaysState } from 'gateways/hooks/useMqttGatewaysState';
import { useGateway } from 'gateways/hooks/useGateway';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';

import * as PageLayout from 'components/PageLayout';

import { RAPID_DEPLOY_VIDEO_SOURCES_PAGE_SIZE } from './components/VideoSourcesList';
import { RapidDeployContext } from './hooks/useRapidDeployState';
import { RapidDeployStepper } from './Stepper';
import { RapidDeployRoute } from './routes';
import styles from './RapidDeploy.module.scss';

const c = classNames.bind(styles);

type ClearReducerParams = {
  type: 'clear';
  data?: never;
};

export type RapidDeployVideoSourcesReducerParams =
  | ClearReducerParams
  | {
      type: 'add' | 'remove' | 'toggle';
      data: VideoSource;
    };

function videoSourcesReducer(
  state: VideoSource[],
  { type, data }: RapidDeployVideoSourcesReducerParams
): VideoSource[] {
  switch (type) {
    case 'toggle':
      if (state.some((source) => source.id === data.id)) {
        return state.filter((source) => source.id !== data.id);
      }
      return [...state, data];
    case 'add':
      if (!state.some((source) => source.id === data.id)) {
        return [...state, data];
      }
      return state;
    case 'remove':
      return state.filter((source) => source.id !== data.id);
    case 'clear':
      return [];
    default:
      return state;
  }
}

export function RapidDeploy() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isRingPlan } = useCurrentPlan();

  // Store preselected pipeline ID from sessionStorage (location state doesn't work with nested routes)
  const [preselectedPipelineId] = React.useState<string | undefined>(() => {
    const stored = sessionStorage.getItem('rapidDeployPreselectedPipelineId');
    return stored || undefined;
  });

  // Clean up sessionStorage when component unmounts
  React.useEffect(() => {
    return () => {
      if (preselectedPipelineId) {
        sessionStorage.removeItem('rapidDeployPreselectedPipelineId');
      }
    };
  }, [preselectedPipelineId]);

  const [gatewayId, setGatewayId] = React.useState<string>();
  const [videoSources, dispatchVideoSources] = React.useReducer(
    videoSourcesReducer,
    []
  );

  // Fetch and subscribe to MQTT updates for selected gateway
  const { data: gateway } = useGateway(gatewayId, {
    enabled: Boolean(gatewayId),
  });
  useMqttGatewaysState(gatewayId ? [gatewayId] : []);

  // Auto-start and preselect Lumeo Cloud OnDemand gateway if present
  const { mutate: startCloudGateway, isLoading: isStartingCloudGateway } =
    useStartCloudGateway();

  const { data: ondemandGatewaysData } = useGateways({
    params: { gateway_models: ['Lumeo Cloud OnDemand'], limit: 1 },
    enabled: !gatewayId,
    queryKey: ['gateways-ondemand-autoselect'],
  });

  React.useEffect(() => {
    if (
      !gatewayId &&
      ondemandGatewaysData?.data &&
      ondemandGatewaysData.data.length > 0 &&
      !isStartingCloudGateway
    ) {
      const [ondemand] = ondemandGatewaysData.data;
      setGatewayId(ondemand.id);
      // Only start if gateway is offline or pending (not already online/starting)
      if (ondemand.status !== 'online' && ondemand.status !== 'starting') {
        startCloudGateway(ondemand.id);
      }
    }
  }, [
    gatewayId,
    ondemandGatewaysData,
    startCloudGateway,
    isStartingCloudGateway,
  ]);

  // Auto-navigate to video sources on Ring plans once a gateway is selected
  React.useEffect(() => {
    const currentRoute = location.pathname.split('/').pop();
    if (
      isRingPlan &&
      gatewayId &&
      currentRoute === RapidDeployRoute.GATEWAY
    ) {
      navigate(RapidDeployRoute.VIDEO_SOURCES, { replace: true });
    }
  }, [isRingPlan, gatewayId, location.pathname, navigate]);

  // Preload video sources with default filters
  useVideoSources({
    params: {
      fetch_type: 'all',
      gateway_ids: gateway ? [gateway.id] : undefined,
      stream_types: ['rtsp', 'webrtc'],
      stream_sources: ['camera_stream', 'uri_stream'],
      // Include all camera connection types (local, virtual, remote) so Ring/Virtual show up
      limit: RAPID_DEPLOY_VIDEO_SOURCES_PAGE_SIZE + 20,
    },
    enabled: Boolean(gateway),
  });

  const updateGateway = React.useCallback((gateway: Gateway) => {
    setGatewayId(gateway.id);
    dispatchVideoSources({ type: 'clear' });
  }, []);

  return (
    <div className={c('rapid-deploy')}>
      <RapidDeployContext.Provider
        value={{
          gateway,
          setGateway: updateGateway,
          videoSources,
          dispatchVideoSources,
          preselectedPipelineId,
        }}
      >
        <PageLayout.Root size="medium">
          <PageLayout.Header>
            <div className={c('header')}>
              <PageLayout.Title>Deploy</PageLayout.Title>
              <RapidDeployStepper />
            </div>
          </PageLayout.Header>

          <Outlet />
        </PageLayout.Root>
      </RapidDeployContext.Provider>
    </div>
  );
}
